import React, { Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RainbowKitProvider } from '@rainbow-me/rainbowkit';
import { HelmetProvider } from 'react-helmet-async';
import '@rainbow-me/rainbowkit/styles.css';
import { config } from './config/wagmi';
import { GlobalStyles } from './styles/GlobalStyles';
import Navbar from './components/Navbar';
import Home from './redesign/Home';
import GovernancePage from './redesign/pages/governance/GovernancePage';
import ENSPage from './redesign/pages/ens/EnsPage';
import ComicPage from './pages/ComicPage';
import ComicEmbedPage from './pages/ComicEmbedPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import Footer from './components/Footer';
import RedesignA from './redesign/a';
import RedesignB from './redesign/b';
import RedesignC from './redesign/c';
import RedesignD from './redesign/d';
import { ByDevice } from './redesign/ByDevice';
import RedesignM1 from './redesign/m1';
import RedesignM2 from './redesign/m2';
import RedesignM3 from './redesign/m3';
import RedesignM4 from './redesign/m4';
import RedesignM5 from './redesign/m5';
import RedesignM6 from './redesign/m6';
import RedesignE from './redesign/e';
import RedesignF from './redesign/f';

const queryClient = new QueryClient();

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      console.error('Error boundary caught:', this.state.error);
      // Don't return null, return children anyway to prevent blocking
      return this.props.children;
    }

    return this.props.children;
  }
}

function AppContent() {
  const location = useLocation();
  const isEmbedPage = location.pathname === '/comic/embed';
  // the home page and the pages drawn in its style bring their own header and styles
  const isDrawnPage = ['/', '/ens', '/governance'].includes(location.pathname) || location.pathname.startsWith('/redesign/');
  const useReactNav = !isEmbedPage && !isDrawnPage;

  return (
    <>
      {!isDrawnPage && <GlobalStyles />}
      {useReactNav && <div className="lightning-bg"></div>}
      {useReactNav && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/governance" element={<GovernancePage />} />
          <Route path="/ens" element={<ENSPage />} />
          <Route path="/comic" element={<ComicPage />} />
          <Route path="/comic/embed" element={<ComicEmbedPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/redesign/a" element={<RedesignA />} />
          <Route path="/redesign/b" element={<RedesignB />} />
          <Route path="/redesign/c" element={<RedesignC />} />
          <Route path="/redesign/d" element={<ByDevice desktop={RedesignD} mobiles={{ m1: RedesignM1, m2: RedesignM2, m3: RedesignM3, m4: RedesignM4, m5: RedesignM5, m6: RedesignM6 }} fallback="m2" />} />
          <Route path="/redesign/m1" element={<RedesignM1 />} />
          <Route path="/redesign/m2" element={<RedesignM2 />} />
          <Route path="/redesign/m3" element={<RedesignM3 />} />
          <Route path="/redesign/m4" element={<RedesignM4 />} />
          <Route path="/redesign/m5" element={<RedesignM5 />} />
          <Route path="/redesign/m6" element={<RedesignM6 />} />
          <Route path="/redesign/e" element={<RedesignE />} />
          <Route path="/redesign/f" element={<RedesignF />} />
        </Routes>
      </main>
      {useReactNav && <Footer />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <WagmiProvider config={config}>
          <QueryClientProvider client={queryClient}>
            <RainbowKitProvider>
              <Router>
                <AppContent />
              </Router>
            </RainbowKitProvider>
          </QueryClientProvider>
        </WagmiProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
